import { ColDef, GridOptions } from '@ag-grid-community/core'
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
} from '@helios/ag-grid-config'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { AccountDetailsKey } from '@helios/panels/account-details'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
  AggregateDetails,
  AggregateDetailsField,
} from '../../models/sleeve-details.model'
import { SleeveDetailsService } from '../../services/sleeve-details.service'

@Component({
  selector: 'hs-aggregate-details-grid',
  templateUrl: './aggregate-details-grid.component.html',
  styleUrls: ['./aggregate-details-grid.component.scss'],
})
export class AggregateDetailsGridComponent implements OnDestroy {
  /**
   * DO NOT CHANGE. Saved Views and Filter Groups depend on this key. Changing will result in users losing their saved
   * preferences for this grid.
   */
  AggregateDetailsGridKey = `${AccountDetailsKey}-aggregateDetailsGrid`
  constructor(
    public sleeveDetailsService: SleeveDetailsService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}
  @Output() showAggregateDetailsEmitter = new EventEmitter<boolean>()
  aggregateDetailsData: AggregateDetails[] = []
  loading = false
  private readonly colDefsAggregateDetails: ColDef[] =
    this.createColumnDefsAggregateDetails()
  gridOptionsAggregateDetails: GridOptions =
    this.createGridOptionsAggregateDetails()

  private readonly destroySubject$ = new Subject<void>()

  @Input()
  set accountSelectionChanged(accountId: number | undefined) {
    if (accountId != undefined) {
      this.loading = true
      this.getAggregateDetails(accountId)
    }
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  createColumnDefsAggregateDetails(): ColDef[] {
    return [
      {
        field: AggregateDetailsField('id'),
        headerName: 'Account ID',
        width: 300,
      },
      {
        field: AggregateDetailsField('name'),
        headerName: 'Account Name',
        width: 500,
      },
    ]
  }
  private createGridOptionsAggregateDetails(): GridOptions {
    return {
      sideBar: false,
      columnDefs: this.colDefsAggregateDetails,
      ...disableChangeDetectionOnScroll(this.changeDetector),
      onGridReady: params => {
        this.savedViewService.manageSavedView(
          params,
          this.AggregateDetailsGridKey,
          this.destroySubject$,
        )
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
        enableHeliosCopyBehavior(params.api)
      },
    }
  }
  async getAggregateDetails(accountId: number) {
    this.aggregateDetailsData = await this.sleeveDetailsService
      .getAggregateDetails(accountId.toString())
      .toPromise()
    this.loading = false
    if (this.aggregateDetailsData.length == 0)
      this.showAggregateDetailsEmitter.emit(false)
  }
}
